import type { Key } from "react";

import { createSelector } from "reselect";

import { selectIsNewOnlyEnabled } from "app/pages/sales-intelligence/modules/advanced-search-filtering/store/selectors";
import type { AppsSearchTableState } from "app/pages/sales-intelligence/modules/apps-search/store/types";
import type { LeadsSearchState } from "app/pages/sales-intelligence/modules/leads-search/store/types";
import {
    type ILeadsSearchFiltersDto,
    selectDirtyFiltersFlag,
    selectFiltersValue,
} from "app/pages/sales-intelligence/modules/leads-search-filters";
import { selectSalesIntelligenceState } from "app/pages/sales-intelligence/store/selectors";
import type { QueryFiltersDto } from "app/pages/sales-intelligence/sub-modules/advanced-search/types/dto";
import type { CompanySearchOtherFilters } from "app/pages/sales-intelligence/sub-modules/company-search/types/common";
import { getCrmName } from "app/pages/sales-intelligence/utils/crm";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import { createStatePropertySelector } from "app/pages/workspace/sales/helpers";
import type { RootState } from "single-spa/store/types";

import { selectContactsSearchStateSlice } from "../../advanced-search/store/selectors";
import { selectRevealedContactsById } from "../../common/store/selectors";
import { selectOtherFilters as selectCompanySearchOtherFilters } from "../../company-search/store/selectors";
import {
    selectDynamicsAccountsToggleState,
    selectDynamicsCrmExportContactsLimit,
    selectDynamicsCrmExportLeadsLimit,
} from "../../management/store/selectors/dynamicsSelectors";
import {
    selectHsCrmExportContactsLimit,
    selectHubspotAccountsToggleState,
} from "../../management/store/selectors/hubspotSelectors";
import {
    selectSalesforceAccountsToggleState,
    selectSfCrmExportContactsLimit,
    selectSfCrmExportLeadsLimit,
} from "../../management/store/selectors/salesforceSelectors";
import type { Integration } from "../../management/types";
import { NEW_ACCOUNT_VALUE } from "../constants/export";
import { toUniqueContactId } from "../helpers/common";
import { isExportFlowActive } from "../helpers/exportFlow";
import type {
    ActionsPanelFilters,
    ContactExportPreview,
    ContactSearchEntry,
    ContactsSearchResults,
    ContactsSearchSortBy,
    ContactsSearchTableFilters,
    CRMEntity,
} from "../types/common";
import type { ContactsSearchResultsRequestDto } from "../types/dto";

const select = createStatePropertySelector(selectContactsSearchStateSlice);

/** Search results  */
export const selectSearchResults = select("searchResults");
export const selectSearchResultsAreFetching = select("areSearchResultsFetching");
const selectResultsTotalCountIsBeingFetched = select("isResultsTotalCountBeingFetched");
export const selectSearchResultsFetchError = select("searchResultsFetchError");
export const selectIsContactUsHookUsed = select("isContactUsHookUsed");
export const selectIsActionPanelActivated = select("isActionPanelActivated");
export const selectSearchResultsRows = (state: RootState) => {
    return selectSearchResults(state).rows;
};
export const selectContactsResultsAreBeingFetched = (state: RootState) => {
    return selectSearchResultsAreFetching(state) || selectResultsTotalCountIsBeingFetched(state);
};
export const selectCorrectTotalCount = (state: RootState) => {
    const { revealedCount, notRevealedCount, totalCount } = selectSearchResults(state);

    if (typeof revealedCount !== "undefined") {
        return revealedCount;
    }

    if (typeof notRevealedCount !== "undefined") {
        return notRevealedCount;
    }

    return totalCount;
};
export const selectRevealedCount = (state: RootState) => {
    const { revealedCount, totalCount } = selectSearchResults(state);

    if (typeof revealedCount !== "undefined") {
        return revealedCount;
    }

    return totalCount;
};
export const selectNotRevealedCount = (state: RootState) => {
    const { notRevealedCount, totalCount } = selectSearchResults(state);

    if (typeof notRevealedCount !== "undefined") {
        return notRevealedCount;
    }

    return totalCount;
};

/** Contacts table filters */
export const selectTableFilters = select("tableFilters");
const selectTableCurrentPage = (state: RootState) => {
    return selectTableFilters(state).page;
};
export const selectTablePageSize = (state: RootState) => {
    return selectTableFilters(state).pageSize;
};
export const selectContactsSearchText = (state: RootState) => {
    return selectTableFilters(state).searchText;
};

export const selectSortingBy = select("sortBy");
/** Actions panel filters */
export const selectActionsPanelFilters = select("actionsPanelFilters");
export const selectIsExportedExcluded = (state: RootState) => {
    return selectActionsPanelFilters(state).excludeExported;
};
const selectIsOnlyNotRevealedShown = (state: RootState) => {
    return selectActionsPanelFilters(state).includeOnlyNotRevealed;
};
export const selectIsOnlyRevealedShown = (state: RootState) => {
    return selectActionsPanelFilters(state).includeOnlyRevealed;
};
export const selectCorrectTableCurrentPage = createSelector(
    [selectTableCurrentPage, selectActionsPanelFilters],
    (currentPage, actionsPanelFilters) => {
        const { page: actionPanelFiltersPage } = actionsPanelFilters;
        return typeof actionPanelFiltersPage === "undefined" ? currentPage : actionPanelFiltersPage;
    },
);

export const selectIsContactsSearchDtoReady = createSelector(
    [selectFiltersValue],
    (queryFilters): boolean =>
        !!(queryFilters && queryFilters.leadsFilters && Object.keys(queryFilters.leadsFilters).length > 0),
);

/** Contacts quota */
const selectQuota = select("quota");
export const selectContactsCost = select("cost");
export const selectQuotaIsBeingFetched = select("isQuotaBeingFetched");
export const selectRemainingQuota = (state: RootState) => {
    return selectQuota(state)?.remaining ?? 0;
};
export const selectTotalQuota = (state: RootState) => {
    return selectQuota(state)?.total ?? 0;
};

/** Contacts reveal */
export const selectMultipleRevealIsInProgress = select("isMultipleRevealInProgress");
export const selectRequestedContactsForExport = select("requestedContactsForExport");
export const selectPendingRevealInfo = select("pendingRevealInfo");
export const selectCrmExportPairs = createSelector(selectRequestedContactsForExport, (contacts: ContactSearchEntry[]) =>
    contacts.map((contact) => ({ contactId: contact.id, prospect: contact.site })),
);
export const selectSearchResultsWithRevealedInfo = createSelector(
    [selectSearchResults, selectRevealedContactsById],
    ({ totalCount, rows }, revealedContactsById): ContactsSearchResults => {
        return {
            totalCount,
            rows: rows.map((contactEntry) => {
                if (contactEntry.id in revealedContactsById) {
                    return {
                        ...contactEntry,
                        isRevealed: true,
                        privateData: revealedContactsById[contactEntry.id],
                    };
                }

                return contactEntry;
            }),
        };
    },
);
/** Modal popups */
export const selectRevealLimitReachedModalPayload = select("revealLimitReachedModal");
export const selectContactIssueModalPayload = select("contactIssueModal");
/** Contacts table rows selection */
export const selectSelectedContacts = select("selectedTableRows");
export const selectCheckedContactIds = createSelector(selectSelectedContacts, (rows): Key[] => {
    return rows.map((contact) => contact.key).filter((v) => !is.undefined(v));
});
export const selectCheckedUniqueContactIds = createSelector(selectSelectedContacts, (rows) => {
    return rows.map(toUniqueContactId);
});
export const selectTableRowsSelectionIsEnabled = select("isRowsSelectionEnabled");
/** Excel export */
export const selectIsBeingExportedToExcel = select("isExportingToExcel");
/** Number of search results */
export const selectNumberOfContactsSearchResults = (state: RootState) => {
    return selectSearchResults(state).totalCount ?? 0;
};

export const selectExportVariant = select("exportVariant");

const selectExportToCrmState = select("exportToCrm");
export const selectCrmToExportContacts = createSelector(selectExportToCrmState, (state) => state.crm);
export const selectCrmNameToExportContacts = createSelector(selectCrmToExportContacts, (crm) => crm && getCrmName(crm));

export const selectIsExportToCrm = createSelector(selectExportVariant, (variant) => variant === "crm");

export const selectExportDestination = createSelector(
    selectExportVariant,
    selectCrmToExportContacts,
    (destination, crm) => (destination === "excel" ? "excel" : crm),
);

export const selectEntityToExportContactsAs = createSelector(selectExportToCrmState, (state) => state.as);
export const selectIsFetchingExportPreview = createSelector(
    selectExportToCrmState,
    (exportState) => exportState.isFetchingPreview,
);
const selectExportContactsPreview = createSelector(
    selectExportToCrmState,
    (exportState) => exportState.contactsPreview,
);
export const selectUnmatchedExportContactsPreview = createSelector(selectExportContactsPreview, (contactsPreview) =>
    contactsPreview.filter((contact) => contact.matchedCompanies.length !== 1),
);
export const selectExistAsLeadExportContactsPreview = createSelector(selectExportContactsPreview, (contactsPreview) =>
    contactsPreview.filter((contact) => contact.isExistAsLead),
);
export const selectCannotBeExportedContactsPreview = createSelector(selectExportContactsPreview, (contactsPreview) =>
    contactsPreview.filter((contact) => contact.isExistAsLead || contact.matchedCompanies.length !== 1),
);
export const selectExportContactsPreviewCount = createSelector(
    selectExportContactsPreview,
    (contacts) => contacts.length,
);

export const selectExportContactsToCrmMapping = createSelector(selectExportContactsPreview, (contacts) => {
    const clearedContacts = contacts.filter((contact: ContactExportPreview) => !contact.isExistAsLead);
    return clearedContacts.map((contact: ContactExportPreview) => ({
        contactId: contact.contactId,
        accountId: contact.accountId === NEW_ACCOUNT_VALUE ? undefined : contact.accountId,
        domain: contact.accountDomain,
    }));
});

export const selectCanCreateNewAccount = createSelector(
    selectCrmToExportContacts,
    selectSalesforceAccountsToggleState,
    selectHubspotAccountsToggleState,
    selectDynamicsAccountsToggleState,
    (crm, canCreateSalesforceAccount, canCreateHubspotAccount, canCreateDynamicsAccount) => {
        if (crm === "salesforce") return canCreateSalesforceAccount;
        if (crm === "dynamics") return canCreateDynamicsAccount;
        return canCreateHubspotAccount;
    },
);

const selectContactsCrmExportLimitObject = createSelector(
    selectSfCrmExportLeadsLimit,
    selectSfCrmExportContactsLimit,
    selectHsCrmExportContactsLimit,
    selectDynamicsCrmExportLeadsLimit,
    selectDynamicsCrmExportContactsLimit,
    (sfLeadsLimit, sfContactsLimit, hsContactsLimit, dynamicsLeadsLimit, dynamicsContactsLimit): Record<Integration, Partial<Record<CRMEntity, number>>> => {
        return {
            salesforce: { lead: sfLeadsLimit, contact: sfContactsLimit },
            hubspot: { contact: hsContactsLimit },
            dynamics: { lead: dynamicsLeadsLimit, contact: dynamicsContactsLimit },
        };
    },
);

export const selectContactsCrmExportLimit = createSelector(
    selectCrmToExportContacts,
    selectEntityToExportContactsAs,
    selectContactsCrmExportLimitObject,
    (crm, entity, exportLimits) => {
        return exportLimits?.[crm as Integration]?.[entity as CRMEntity] ?? Infinity;
    },
);

export const selectExportMessageTranslationKeys = createSelector(
    selectEntityToExportContactsAs,
    selectCrmToExportContacts,
    (exportAs, crm) => {
        const asLeadSuffix = exportAs === "lead" ? "_as_lead" : "";
        const keyBase = `si.contacts_search.export_to_crm${asLeadSuffix}.toast`;
        return { crmName: crm ? getCrmName(crm) : "", keyBase };
    },
);

// FIXME selecting the slice to prevent module cyclic dependency
const selectLeadsSearchSlice = createStatePropertySelector<LeadsSearchState>(
    selectSalesIntelligenceState("leadsSearch"),
);
const selectLeadsSearchText = selectLeadsSearchSlice("searchText");
const selectLeadsIsAggregatedDataUsed = selectLeadsSearchSlice("isAggregatedDataUsed");

const selectAppsSearchText = createStatePropertySelector<AppsSearchTableState>(
    selectSalesIntelligenceState("appsSearch"),
)("searchText");

export const selectContactSearchResultsDto = createSelector(
    [
        selectFiltersValue,
        selectLeadsSearchText,
        selectTableFilters,
        selectCompanySearchOtherFilters,
        selectActionsPanelFilters,
        selectContactsSearchText,
        selectIsNewOnlyEnabled,
        selectSortingBy,
        selectAppsSearchText,
    ],
    (
        queryFilters: Partial<ILeadsSearchFiltersDto>,
        leadsSearchText: string,
        tableFilters: ContactsSearchTableFilters,
        companyOtherFilter: CompanySearchOtherFilters,
        actionsPanelFilters: ActionsPanelFilters,
        searchText: string,
        isNewOnly: boolean,
        sortBy: ContactsSearchSortBy | undefined,
        appsSearchText: string,
    ): ContactsSearchResultsRequestDto => {
        const { searchText: _, ...restTableFilters } = tableFilters;

        const actionsPanelFiltersDto = { ...actionsPanelFilters };
        delete actionsPanelFiltersDto.page;

        const isExcludeUserLeads =
            queryFilters?.leadsFilters?.staticListsToExclude &&
            queryFilters?.leadsFilters?.staticListsToExclude.length > 0;

        const contactSearchTextModel = searchText ? { searchText } : {};
        const leadsSearchTextModel = leadsSearchText ? { searchText: leadsSearchText } : {};
        const appsSearchTextModel = appsSearchText ? { searchText: appsSearchText } : {};
        const sortingBy = sortBy ? sortBy : {};

        return {
            ...restTableFilters,
            ...companyOtherFilter,
            ...sortingBy,
            queryFilters: {
                ...((queryFilters || {}) as QueryFiltersDto),
                leadsFilters: {
                    ...(queryFilters?.leadsFilters || {}),
                    ...leadsSearchTextModel,
                },
                contactsFilters: {
                    ...(queryFilters?.contactsFilters || {}),
                    ...contactSearchTextModel,
                },
                appsFilters: {
                    ...(queryFilters?.appsFilters || {}),
                    ...appsSearchTextModel,
                },
            },
            excludeUserLeads: Boolean(isExcludeUserLeads),
            isNewOnly,
            ...actionsPanelFiltersDto,
        };
    },
);

export const selectContactSearchResultsDtoWithAggregation = createSelector(
    [selectContactSearchResultsDto, selectLeadsIsAggregatedDataUsed],
    (dto, leadGenTrafficAggregationEnabled): ContactsSearchResultsRequestDto => ({
        ...dto,
        leadGenTrafficAggregationEnabled,
    }),
);

export const selectLeadsSearchContactsSearchRequestDto = createSelector(
    [selectContactSearchResultsDto, selectActionsPanelFilters],
    (dtoParams, actionsPanelFilters?: ActionsPanelFilters): ContactsSearchResultsRequestDto => {
        const actionsPanelFiltersDto = { ...actionsPanelFilters };
        delete actionsPanelFiltersDto.page;

        return {
            ...dtoParams,
            ...actionsPanelFiltersDto,
        };
    },
);

/** Export flow phase & origin */
export const selectExportFlowPhase = select("exportFlowPhase");
export const selectExportFlowOrigin = select("exportFlowOrigin");

export const selectIsExportFlowActive = createSelector(selectExportFlowPhase, isExportFlowActive);

export const selectIsPanelExportActive = createSelector(
    [selectExportFlowPhase, selectExportFlowOrigin],
    (phase, origin) => isExportFlowActive(phase) && origin === "panel",
);
export const selectIsRowExportActive = createSelector(
    [selectExportFlowPhase, selectExportFlowOrigin],
    (phase, origin) => isExportFlowActive(phase) && origin === "row",
);

export const selectAreAnyContactsFiltersApplied = createSelector(
    [selectDirtyFiltersFlag, selectContactsSearchText],
    (hasAnyLeadsSearchFilter, leadSearchContactsSearchText): boolean =>
        hasAnyLeadsSearchFilter || !!leadSearchContactsSearchText,
);
